import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Building2, 
  FileKey, 
  Calculator, 
  Users, 
  MessageSquare, 
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Upload,
  AlertCircle,
  Sparkles,
  Download,
  CreditCard,
  Link,
  Loader2,
  Check,
  X,
  FileText,
  Gift
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SetupWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Company data
  const [companyData, setCompanyData] = useState({
    rfc: '',
    razon_social: '',
    regimen_fiscal: '',
    codigo_postal: '',
    email: '',
    phone: ''
  });
  
  // CSD files
  const [csdCer, setCsdCer] = useState(null);
  const [csdKey, setCsdKey] = useState(null);
  const [csdPassword, setCsdPassword] = useState('');
  
  // FIEL files
  const [fielCer, setFielCer] = useState(null);
  const [fielKey, setFielKey] = useState(null);
  const [fielPassword, setFielPassword] = useState('');
  
  // Tax config
  const [taxConfig, setTaxConfig] = useState({
    iva_rate: '0.160000',
    is_frontier: false,
    retenciones_enabled: true
  });
  
  // First client
  const [clientData, setClientData] = useState({
    rfc: '',
    razon_social: '',
    regimen_fiscal: '',
    codigo_postal: '',
    email: '',
    uso_cfdi: 'G03'
  });
  
  // WhatsApp config
  const [whatsappConfig, setWhatsappConfig] = useState({
    preferred_name: '',
    csf_reminder: 'monthly',
    opinion_reminder: 'monthly',
    phone: ''
  });
  
  // Billing data (para facturarle al cliente)
  const [billingData, setBillingData] = useState({
    rfc: '',
    razon_social: '',
    regimen_fiscal: '',
    codigo_postal: '',
    email: '',
    uso_cfdi: 'G03'
  });
  
  // Referral code
  const [referralCode, setReferralCode] = useState('');
  
  // Setup status
  const [setupStatus, setSetupStatus] = useState({
    company: false,
    certificates: false,
    taxConfig: false,
    firstClient: false,
    subscription: false,
    billingData: false,
    whatsapp: false
  });
  
  // Subscription status
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  
  // Facturama status
  const [facturamaStatus, setFacturamaStatus] = useState(null);
  
  // Certificate status
  const [certStatus, setCertStatus] = useState({
    csd_uploaded: false,
    fiel_uploaded: false
  });

  const steps = [
    {
      id: 'welcome',
      title: '¡Bienvenido a CIA Factura℗!',
      description: 'Configuremos tu cuenta en 7 sencillos pasos',
      icon: Sparkles,
      color: 'text-purple-500'
    },
    {
      id: 'company',
      title: 'Datos de tu Empresa',
      description: 'RFC, razón social y datos fiscales',
      icon: Building2,
      color: 'text-blue-500'
    },
    {
      id: 'certificates',
      title: 'Certificados CSD y FIEL',
      description: 'Para timbrar facturas y descargar documentos del SAT',
      icon: FileKey,
      color: 'text-green-500'
    },
    {
      id: 'subscription',
      title: 'Suscripción',
      description: 'Selecciona tu plan para comenzar a facturar',
      icon: CreditCard,
      color: 'text-indigo-500'
    },
    {
      id: 'billingData',
      title: 'Datos para tu Factura',
      description: 'Para emitirte factura por tu suscripción',
      icon: FileText,
      color: 'text-emerald-500'
    },
    {
      id: 'taxConfig',
      title: 'Configuración Fiscal',
      description: 'IVA, retenciones e impuestos locales',
      icon: Calculator,
      color: 'text-orange-500'
    },
    {
      id: 'firstClient',
      title: 'Tu Primer Cliente',
      description: 'Registra un cliente para empezar a facturar',
      icon: Users,
      color: 'text-pink-500'
    },
    {
      id: 'whatsapp',
      title: 'WhatsApp Bot',
      description: 'Configura tu asistente de facturación',
      icon: MessageSquare,
      color: 'text-teal-500'
    },
    {
      id: 'complete',
      title: '¡Todo Listo!',
      description: 'Tu cuenta está configurada',
      icon: CheckCircle2,
      color: 'text-green-500'
    }
  ];

  // Fetch initial status
  useEffect(() => {
    fetchSetupStatus();
  }, []);

  const fetchSetupStatus = async () => {
    try {
      const token = localStorage.getItem('cia_token');
      
      // Fetch onboarding status
      const [onboardingRes, certRes, subRes] = await Promise.all([
        fetch(`${API_URL}/api/companies/onboarding`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/certificates/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/subscription`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (onboardingRes.ok) {
        const data = await onboardingRes.json();
        setSetupStatus(prev => ({
          ...prev,
          company: data.steps?.company_created || false,
          firstClient: data.steps?.first_client_added || false
        }));
        
        // If company exists, fetch its data
        if (data.steps?.company_created) {
          const companyRes = await fetch(`${API_URL}/api/companies/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (companyRes.ok) {
            const company = await companyRes.json();
            setCompanyData({
              rfc: company.rfc || '',
              razon_social: company.razon_social || '',
              regimen_fiscal: company.regimen_fiscal || '',
              codigo_postal: company.codigo_postal || '',
              email: company.email || '',
              phone: company.phone || ''
            });
          }
        }
      }
      
      if (certRes.ok) {
        const certData = await certRes.json();
        setCertStatus({
          csd_uploaded: certData.csd?.uploaded || false,
          fiel_uploaded: certData.fiel?.uploaded || false
        });
        setSetupStatus(prev => ({
          ...prev,
          certificates: certData.csd?.uploaded || false
        }));
      }
      
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscriptionStatus(subData);
        setSetupStatus(prev => ({
          ...prev,
          subscription: subData.status === 'active' || subData.status === 'trialing'
        }));
      }
      
    } catch (error) {
      console.error('Error fetching setup status:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const progress = ((currentStep) / (steps.length - 1)) * 100;

  // Handlers
  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('cia_token');
      
      // Validate required fields
      if (!companyData.rfc || !companyData.razon_social || !companyData.regimen_fiscal || 
          !companyData.codigo_postal || !companyData.email || !companyData.phone) {
        toast.error('Por favor completa todos los campos obligatorios');
        setSaving(false);
        return;
      }
      
      const endpoint = setupStatus.company ? '/api/companies/me' : '/api/companies';
      const method = setupStatus.company ? 'PUT' : 'POST';
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: companyData.razon_social,
          ...companyData
        })
      });
      
      if (response.ok) {
        toast.success('Datos de empresa guardados');
        setSetupStatus(prev => ({ ...prev, company: true }));
        setCurrentStep(2);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadCSD = async () => {
    if (!csdCer || !csdKey || !csdPassword) {
      toast.error('Selecciona ambos archivos y la contraseña del CSD');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('cia_token');
      const formData = new FormData();
      formData.append('cer_file', csdCer);
      formData.append('key_file', csdKey);
      formData.append('password', csdPassword);
      
      const response = await fetch(`${API_URL}/api/certificates/csd`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('CSD subido correctamente');
        setCertStatus(prev => ({ ...prev, csd_uploaded: true }));
        setSetupStatus(prev => ({ ...prev, certificates: true }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al subir CSD');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadFIEL = async () => {
    if (!fielCer || !fielKey || !fielPassword) {
      toast.error('Selecciona ambos archivos y la contraseña de la FIEL');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('cia_token');
      const formData = new FormData();
      formData.append('cer_file', fielCer);
      formData.append('key_file', fielKey);
      formData.append('password', fielPassword);
      
      const response = await fetch(`${API_URL}/api/certificates/fiel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('FIEL subida correctamente');
        setCertStatus(prev => ({ ...prev, fiel_uploaded: true }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al subir FIEL');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClient = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('cia_token');
      
      if (!clientData.rfc || !clientData.razon_social || !clientData.email) {
        toast.error('Completa los campos obligatorios del cliente');
        setSaving(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/api/clients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: clientData.razon_social,
          ...clientData
        })
      });
      
      if (response.ok) {
        toast.success('Cliente registrado');
        setSetupStatus(prev => ({ ...prev, firstClient: true }));
        setCurrentStep(7);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al registrar cliente');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWhatsAppConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('cia_token');
      
      // Save preferences
      const response = await fetch(`${API_URL}/api/whatsapp/preferences`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(whatsappConfig)
      });
      
      if (response.ok) {
        toast.success('Configuración de WhatsApp guardada');
        setSetupStatus(prev => ({ ...prev, whatsapp: true }));
      }
      
      // Complete onboarding
      await fetch(`${API_URL}/api/companies/onboarding/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setCurrentStep(8);
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleStartSubscription = async (planId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('cia_token');
      const response = await fetch(`${API_URL}/api/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al iniciar pago');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // File input refs
  const csdCerRef = useRef(null);
  const csdKeyRef = useRef(null);
  const fielCerRef = useRef(null);
  const fielKeyRef = useRef(null);

  // Render functions for each step
  const renderWelcome = () => (
    <div className="text-center space-y-6 py-8" data-testid="setup-welcome">
      <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mx-auto flex items-center justify-center">
        <Sparkles className="w-12 h-12 text-white" />
      </div>
      <h2 className="text-3xl font-bold text-gray-900">¡Bienvenido a CIA Factura℗!</h2>
      <p className="text-gray-600 max-w-md mx-auto">
        Vamos a configurar tu cuenta paso a paso. Este proceso toma aproximadamente 10-15 minutos.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto mt-8">
        <div className="bg-blue-50 p-4 rounded-lg text-left">
          <h4 className="font-semibold text-blue-900">Lo que necesitas:</h4>
          <ul className="text-sm text-blue-700 mt-2 space-y-1">
            <li>• RFC y datos fiscales de tu empresa</li>
            <li>• Archivos CSD (.cer y .key) + contraseña</li>
            <li>• Archivos FIEL (.cer y .key) + contraseña</li>
            <li>• Tarjeta de crédito/débito para suscripción</li>
            <li>• Datos de al menos un cliente</li>
          </ul>
        </div>
        <div className="bg-green-50 p-4 rounded-lg text-left">
          <h4 className="font-semibold text-green-900">Lo que obtendrás:</h4>
          <ul className="text-sm text-green-700 mt-2 space-y-1">
            <li>• Facturación electrónica CFDI 4.0</li>
            <li>• Bot de WhatsApp personalizado</li>
            <li>• Descarga automática de CSF</li>
            <li>• Recordatorios inteligentes</li>
            <li>• Soporte técnico incluido</li>
          </ul>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-2xl mx-auto">
        <div className="flex items-start gap-3">
          <MessageSquare className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="text-left">
            <h4 className="font-medium text-amber-900">¿Prefieres configurar desde WhatsApp?</h4>
            <p className="text-sm text-amber-700 mt-1">
              Puedes hacer toda esta configuración e incluso pagar tu suscripción directamente desde WhatsApp.
              Solo escribe al <strong>+1 415 523 8886</strong> con el código <strong>"join yearly-shape"</strong>.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-center gap-4 mt-8">
        <Button 
          variant="outline"
          onClick={() => window.open(`${API_URL}/api/docs/manual`, '_blank')}
          data-testid="download-manual-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Descargar Manual PDF
        </Button>
        <Button onClick={() => setCurrentStep(1)} data-testid="start-setup-btn">
          Comenzar Configuración
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderCompanyStep = () => (
    <div className="space-y-6" data-testid="setup-company">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">Datos del SAT</h4>
            <p className="text-sm text-blue-700">
              Asegúrate de que los datos coincidan exactamente con tu Constancia de Situación Fiscal.
            </p>
          </div>
        </div>
      </div>

      {setupStatus.company && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            <div>
              <h4 className="font-medium text-green-900">Datos ya configurados</h4>
              <p className="text-sm text-green-700">
                Puedes actualizar los datos si es necesario o continuar al siguiente paso.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>RFC *</Label>
            <Input 
              placeholder="XAXX010101000" 
              value={companyData.rfc}
              onChange={(e) => setCompanyData(prev => ({ ...prev, rfc: e.target.value.toUpperCase() }))}
              maxLength={13}
              data-testid="company-rfc-input"
            />
          </div>
          <div>
            <Label>Régimen Fiscal *</Label>
            <select 
              className="w-full border rounded-md p-2 h-10"
              value={companyData.regimen_fiscal}
              onChange={(e) => setCompanyData(prev => ({ ...prev, regimen_fiscal: e.target.value }))}
              data-testid="company-regimen-select"
            >
              <option value="">Selecciona...</option>
              <option value="601">601 - General de Ley PM</option>
              <option value="603">603 - Personas Morales sin fines lucrativos</option>
              <option value="606">606 - Arrendamiento</option>
              <option value="612">612 - Actividades empresariales y profesionales</option>
              <option value="621">621 - Incorporación Fiscal</option>
              <option value="625">625 - Plataformas Tecnológicas</option>
              <option value="626">626 - Régimen Simplificado de Confianza</option>
            </select>
          </div>
        </div>
        <div>
          <Label>Razón Social *</Label>
          <Input 
            placeholder="Mi Empresa SA de CV" 
            value={companyData.razon_social}
            onChange={(e) => setCompanyData(prev => ({ ...prev, razon_social: e.target.value }))}
            data-testid="company-razon-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Código Postal *</Label>
            <Input 
              placeholder="44100" 
              value={companyData.codigo_postal}
              onChange={(e) => setCompanyData(prev => ({ ...prev, codigo_postal: e.target.value }))}
              maxLength={5}
              data-testid="company-cp-input"
            />
          </div>
          <div>
            <Label>Email *</Label>
            <Input 
              type="email" 
              placeholder="contacto@miempresa.com"
              value={companyData.email}
              onChange={(e) => setCompanyData(prev => ({ ...prev, email: e.target.value }))}
              data-testid="company-email-input"
            />
          </div>
        </div>
        <div>
          <Label>Teléfono WhatsApp *</Label>
          <Input 
            placeholder="+52 33 1234 5678"
            value={companyData.phone}
            onChange={(e) => setCompanyData(prev => ({ ...prev, phone: e.target.value }))}
            data-testid="company-phone-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            Este número se usará para recibir notificaciones y hablar con el bot de facturación.
          </p>
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setCurrentStep(0)}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Atrás
        </Button>
        <Button onClick={handleSaveCompany} disabled={saving} data-testid="save-company-btn">
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
          ) : (
            <>{setupStatus.company ? 'Actualizar y Continuar' : 'Guardar y Continuar'}<ChevronRight className="w-4 h-4 ml-2" /></>
          )}
        </Button>
      </div>
    </div>
  );

  const renderCertificatesStep = () => (
    <div className="space-y-6" data-testid="setup-certificates">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-900">¿Dónde obtengo mis certificados?</h4>
            <p className="text-sm text-amber-700">
              Descarga tus CSD y FIEL desde el portal del SAT: 
              <a href="https://www.sat.gob.mx" target="_blank" rel="noopener noreferrer" className="underline ml-1">
                sat.gob.mx
              </a>
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* CSD */}
        <Card className={certStatus.csd_uploaded ? 'border-green-300 bg-green-50' : ''}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileKey className="w-5 h-5 text-blue-500" />
              CSD (Certificado de Sello Digital)
              {certStatus.csd_uploaded && <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />}
            </CardTitle>
            <CardDescription>Para timbrar facturas electrónicas</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {certStatus.csd_uploaded ? (
              <div className="text-center py-4">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="text-green-700 font-medium">CSD cargado correctamente</p>
              </div>
            ) : (
              <>
                <div>
                  <Label>Archivo .cer</Label>
                  <input
                    ref={csdCerRef}
                    type="file"
                    accept=".cer"
                    onChange={(e) => setCsdCer(e.target.files[0])}
                    className="hidden"
                  />
                  <div 
                    onClick={() => csdCerRef.current.click()}
                    className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
                      ${csdCer ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-blue-400'}`}
                  >
                    {csdCer ? (
                      <div className="flex items-center justify-center gap-2 text-green-600">
                        <Check className="w-5 h-5" />
                        <span>{csdCer.name}</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Click para subir .cer</p>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Archivo .key</Label>
                  <input
                    ref={csdKeyRef}
                    type="file"
                    accept=".key"
                    onChange={(e) => setCsdKey(e.target.files[0])}
                    className="hidden"
                  />
                  <div 
                    onClick={() => csdKeyRef.current.click()}
                    className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
                      ${csdKey ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-blue-400'}`}
                  >
                    {csdKey ? (
                      <div className="flex items-center justify-center gap-2 text-green-600">
                        <Check className="w-5 h-5" />
                        <span>{csdKey.name}</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Click para subir .key</p>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Contraseña del CSD</Label>
                  <Input 
                    type="password" 
                    placeholder="Contraseña"
                    value={csdPassword}
                    onChange={(e) => setCsdPassword(e.target.value)}
                  />
                </div>
                <Button 
                  onClick={handleUploadCSD} 
                  disabled={saving || !csdCer || !csdKey || !csdPassword}
                  className="w-full"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Subir CSD
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* FIEL */}
        <Card className={certStatus.fiel_uploaded ? 'border-green-300 bg-green-50' : ''}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileKey className="w-5 h-5 text-green-500" />
              FIEL (e.Firma)
              {certStatus.fiel_uploaded && <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />}
            </CardTitle>
            <CardDescription>Para descargar CSF y Opinión 32-D automáticamente</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {certStatus.fiel_uploaded ? (
              <div className="text-center py-4">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="text-green-700 font-medium">FIEL cargada correctamente</p>
              </div>
            ) : (
              <>
                <div>
                  <Label>Archivo .cer</Label>
                  <input
                    ref={fielCerRef}
                    type="file"
                    accept=".cer"
                    onChange={(e) => setFielCer(e.target.files[0])}
                    className="hidden"
                  />
                  <div 
                    onClick={() => fielCerRef.current.click()}
                    className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
                      ${fielCer ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-green-400'}`}
                  >
                    {fielCer ? (
                      <div className="flex items-center justify-center gap-2 text-green-600">
                        <Check className="w-5 h-5" />
                        <span>{fielCer.name}</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Click para subir .cer</p>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Archivo .key</Label>
                  <input
                    ref={fielKeyRef}
                    type="file"
                    accept=".key"
                    onChange={(e) => setFielKey(e.target.files[0])}
                    className="hidden"
                  />
                  <div 
                    onClick={() => fielKeyRef.current.click()}
                    className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
                      ${fielKey ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-green-400'}`}
                  >
                    {fielKey ? (
                      <div className="flex items-center justify-center gap-2 text-green-600">
                        <Check className="w-5 h-5" />
                        <span>{fielKey.name}</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Click para subir .key</p>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Contraseña de la FIEL</Label>
                  <Input 
                    type="password" 
                    placeholder="Contraseña"
                    value={fielPassword}
                    onChange={(e) => setFielPassword(e.target.value)}
                  />
                </div>
                <Button 
                  onClick={handleUploadFIEL} 
                  disabled={saving || !fielCer || !fielKey || !fielPassword}
                  className="w-full"
                  variant="outline"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Subir FIEL
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setCurrentStep(1)}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Atrás
        </Button>
        <div className="flex gap-2">
          {!certStatus.csd_uploaded && (
            <Button variant="ghost" onClick={() => setCurrentStep(3)}>
              Omitir por ahora
            </Button>
          )}
          <Button 
            onClick={() => setCurrentStep(3)} 
            disabled={!certStatus.csd_uploaded}
            data-testid="continue-from-certs-btn"
          >
            Continuar
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );

  const renderSubscriptionStep = () => (
    <div className="space-y-6" data-testid="setup-subscription">
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-indigo-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-indigo-900">Selecciona tu Plan</h4>
            <p className="text-sm text-indigo-700">
              Todos los planes incluyen 7 días de prueba gratis. Cancela cuando quieras.
            </p>
          </div>
        </div>
      </div>

      {setupStatus.subscription ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h4 className="font-semibold text-green-900 text-lg">¡Suscripción Activa!</h4>
          <p className="text-green-700">Tu plan está configurado y listo para usar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Plan Básico */}
          <Card className="relative hover:border-blue-400 transition-colors cursor-pointer">
            <CardHeader>
              <CardTitle className="text-lg">Básico</CardTitle>
              <div className="text-3xl font-bold text-gray-900">
                $299<span className="text-sm font-normal text-gray-500">/mes</span>
              </div>
              <CardDescription>Ideal para freelancers</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 50 facturas/mes</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 1 RFC</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Bot WhatsApp</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Soporte email</li>
              </ul>
              <Button 
                className="w-full mt-4" 
                variant="outline"
                onClick={() => handleStartSubscription('basic')}
                disabled={loading}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Seleccionar'}
              </Button>
            </CardContent>
          </Card>

          {/* Plan Pro */}
          <Card className="relative border-2 border-blue-500 hover:border-blue-600 transition-colors cursor-pointer">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs px-3 py-1 rounded-full">
              Más Popular
            </div>
            <CardHeader>
              <CardTitle className="text-lg">Profesional</CardTitle>
              <div className="text-3xl font-bold text-gray-900">
                $599<span className="text-sm font-normal text-gray-500">/mes</span>
              </div>
              <CardDescription>Para pequeñas empresas</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 200 facturas/mes</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 1 RFC</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Bot WhatsApp premium</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Descarga automática CSF</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Soporte prioritario</li>
              </ul>
              <Button 
                className="w-full mt-4"
                onClick={() => handleStartSubscription('professional')}
                disabled={loading}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Seleccionar'}
              </Button>
            </CardContent>
          </Card>

          {/* Plan Empresarial */}
          <Card className="relative hover:border-purple-400 transition-colors cursor-pointer">
            <CardHeader>
              <CardTitle className="text-lg">Empresarial</CardTitle>
              <div className="text-3xl font-bold text-gray-900">
                $1,199<span className="text-sm font-normal text-gray-500">/mes</span>
              </div>
              <CardDescription>Para empresas con varios RFCs</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Facturas ilimitadas</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Hasta 5 RFCs</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Multi-usuario</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> API pública</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Soporte 24/7</li>
              </ul>
              <Button 
                className="w-full mt-4" 
                variant="outline"
                onClick={() => handleStartSubscription('enterprise')}
                disabled={loading}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Seleccionar'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="bg-gray-50 border rounded-lg p-4 text-center">
        <p className="text-sm text-gray-600">
          <CreditCard className="w-4 h-4 inline mr-1" />
          Pago seguro procesado por Stripe. También puedes pagar desde WhatsApp escribiendo <strong>"suscripción"</strong> al bot.
        </p>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setCurrentStep(2)}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Atrás
        </Button>
        {setupStatus.subscription && (
          <Button onClick={() => setCurrentStep(4)} data-testid="continue-from-sub-btn">
            Continuar
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );

  const handleSaveBillingData = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('cia_token');
      
      const response = await fetch(`${API_URL}/api/billing-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(billingData)
      });
      
      if (response.ok) {
        toast.success('Datos de facturación guardados');
        setSetupStatus(prev => ({ ...prev, billingData: true }));
        setCurrentStep(5);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al guardar datos');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyReferral = async () => {
    if (!referralCode.trim()) return;
    
    try {
      const token = localStorage.getItem('cia_token');
      const response = await fetch(`${API_URL}/api/referrals/apply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ referral_code: referralCode })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Código no válido');
      }
    } catch (error) {
      toast.error('Error al aplicar código');
    }
  };

  const renderBillingDataStep = () => (
    <div className="space-y-6" data-testid="setup-billing">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-emerald-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-emerald-900">Datos para tu Factura</h4>
            <p className="text-sm text-emerald-700">
              Ingresa tus datos fiscales para que te emitamos factura por tu suscripción automáticamente.
            </p>
          </div>
        </div>
      </div>

      {setupStatus.billingData ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h4 className="font-semibold text-green-900 text-lg">¡Datos Guardados!</h4>
          <p className="text-green-700">Recibirás tu factura automáticamente por email.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>RFC *</Label>
              <Input 
                placeholder="XAXX010101000"
                value={billingData.rfc}
                onChange={(e) => setBillingData(prev => ({ ...prev, rfc: e.target.value.toUpperCase() }))}
                maxLength={13}
              />
            </div>
            <div>
              <Label>Razón Social *</Label>
              <Input 
                placeholder="Tu Empresa SA de CV"
                value={billingData.razon_social}
                onChange={(e) => setBillingData(prev => ({ ...prev, razon_social: e.target.value }))}
              />
            </div>
            <div>
              <Label>Régimen Fiscal *</Label>
              <select 
                className="w-full border rounded-md p-2"
                value={billingData.regimen_fiscal}
                onChange={(e) => setBillingData(prev => ({ ...prev, regimen_fiscal: e.target.value }))}
              >
                <option value="">Selecciona...</option>
                <option value="601">601 - General de Ley PM</option>
                <option value="603">603 - Personas Morales con Fines no Lucrativos</option>
                <option value="605">605 - Sueldos y Salarios</option>
                <option value="606">606 - Arrendamiento</option>
                <option value="607">607 - Régimen de Enajenación o Adquisición de Bienes</option>
                <option value="608">608 - Demás ingresos</option>
                <option value="610">610 - Residentes en el Extranjero</option>
                <option value="611">611 - Ingresos por Dividendos</option>
                <option value="612">612 - Personas Físicas con Actividades Empresariales</option>
                <option value="614">614 - Ingresos por intereses</option>
                <option value="615">615 - Régimen de los ingresos por obtención de premios</option>
                <option value="616">616 - Sin obligaciones fiscales</option>
                <option value="620">620 - Sociedades Cooperativas de Producción</option>
                <option value="621">621 - Incorporación Fiscal</option>
                <option value="622">622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras</option>
                <option value="623">623 - Opcional para Grupos de Sociedades</option>
                <option value="624">624 - Coordinados</option>
                <option value="625">625 - RESICO (Simplificado de Confianza)</option>
                <option value="626">626 - RESICO PM</option>
              </select>
            </div>
            <div>
              <Label>Código Postal *</Label>
              <Input 
                placeholder="06600"
                value={billingData.codigo_postal}
                onChange={(e) => setBillingData(prev => ({ ...prev, codigo_postal: e.target.value }))}
                maxLength={5}
              />
            </div>
            <div>
              <Label>Email para factura *</Label>
              <Input 
                type="email"
                placeholder="facturacion@tuempresa.com"
                value={billingData.email}
                onChange={(e) => setBillingData(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div>
              <Label>Uso de CFDI</Label>
              <select 
                className="w-full border rounded-md p-2"
                value={billingData.uso_cfdi}
                onChange={(e) => setBillingData(prev => ({ ...prev, uso_cfdi: e.target.value }))}
              >
                <option value="G03">G03 - Gastos en general</option>
                <option value="G01">G01 - Adquisición de mercancías</option>
                <option value="G02">G02 - Devoluciones, descuentos o bonificaciones</option>
                <option value="I01">I01 - Construcciones</option>
                <option value="I02">I02 - Mobiliario y equipo de oficina</option>
                <option value="I03">I03 - Equipo de transporte</option>
                <option value="I04">I04 - Equipo de cómputo</option>
                <option value="I08">I08 - Otra maquinaria y equipo</option>
                <option value="D01">D01 - Honorarios médicos</option>
                <option value="D02">D02 - Gastos médicos por incapacidad</option>
                <option value="D03">D03 - Gastos funerales</option>
                <option value="D04">D04 - Donativos</option>
                <option value="D05">D05 - Intereses por créditos hipotecarios</option>
                <option value="D06">D06 - Aportaciones voluntarias SAR</option>
                <option value="D07">D07 - Primas por seguros de gastos médicos</option>
                <option value="D08">D08 - Gastos de transportación escolar</option>
                <option value="D09">D09 - Depósitos en cuentas de ahorro</option>
                <option value="D10">D10 - Pagos por servicios educativos</option>
                <option value="S01">S01 - Sin efectos fiscales</option>
                <option value="CP01">CP01 - Pagos</option>
              </select>
            </div>
          </div>

          <Button 
            onClick={handleSaveBillingData} 
            disabled={saving || !billingData.rfc || !billingData.razon_social || !billingData.regimen_fiscal || !billingData.codigo_postal || !billingData.email}
            className="w-full"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Guardar Datos de Facturación
          </Button>
        </div>
      )}

      {/* Referral Code Section */}
      <Card className="border-dashed border-2 border-purple-200 bg-purple-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-3">
            <Gift className="w-5 h-5 text-purple-500" />
            <h4 className="font-medium text-purple-900">¿Tienes código de referido?</h4>
          </div>
          <p className="text-sm text-purple-700 mb-3">
            Si alguien te invitó, ingresa su código para obtener 20% de descuento en tu primer pago.
          </p>
          <div className="flex gap-2">
            <Input 
              placeholder="Código de referido"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              className="flex-1"
            />
            <Button variant="outline" onClick={handleApplyReferral} disabled={!referralCode.trim()}>
              Aplicar
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setCurrentStep(3)}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Atrás
        </Button>
        <div className="flex gap-2">
          {!setupStatus.billingData && (
            <Button variant="ghost" onClick={() => setCurrentStep(5)}>
              Omitir por ahora
            </Button>
          )}
          <Button 
            onClick={() => setCurrentStep(5)} 
            disabled={!setupStatus.billingData}
            data-testid="continue-from-billing-btn"
          >
            Continuar
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );

  const renderTaxConfigStep = () => (
    <div className="space-y-6" data-testid="setup-tax">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Calculator className="w-5 h-5 text-orange-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-orange-900">Configuración de Impuestos</h4>
            <p className="text-sm text-orange-700">
              Configura las tasas de IVA, retenciones e impuestos locales según tu actividad.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">IVA</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Tasa de IVA por defecto</Label>
              <select 
                className="w-full border rounded-md p-2"
                value={taxConfig.iva_rate}
                onChange={(e) => setTaxConfig(prev => ({ ...prev, iva_rate: e.target.value }))}
              >
                <option value="0.160000">16% - Tasa General</option>
                <option value="0.080000">8% - Zona Fronteriza</option>
                <option value="0.000000">0% - Tasa Cero</option>
                <option value="exento">Exento</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="frontier" 
                className="rounded"
                checked={taxConfig.is_frontier}
                onChange={(e) => setTaxConfig(prev => ({ ...prev, is_frontier: e.target.checked }))}
              />
              <Label htmlFor="frontier">¿Estás en zona fronteriza?</Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Retenciones</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="retEnabled" 
                className="rounded"
                checked={taxConfig.retenciones_enabled}
                onChange={(e) => setTaxConfig(prev => ({ ...prev, retenciones_enabled: e.target.checked }))}
              />
              <Label htmlFor="retEnabled">Habilitar retenciones</Label>
            </div>
            {taxConfig.retenciones_enabled && (
              <>
                <div className="flex items-center gap-2 pl-6">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">Retención ISR (10%)</span>
                </div>
                <div className="flex items-center gap-2 pl-6">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">Retención IVA (10.67%)</span>
                </div>
              </>
            )}
            <p className="text-xs text-gray-500">
              Las retenciones aplican cuando facturas a Personas Morales
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setCurrentStep(4)}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Atrás
        </Button>
        <Button onClick={() => setCurrentStep(6)} data-testid="continue-from-tax-btn">
          Continuar
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderFirstClientStep = () => (
    <div className="space-y-6" data-testid="setup-client">
      <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 text-pink-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-pink-900">Tu Primer Cliente</h4>
            <p className="text-sm text-pink-700">
              Registra los datos de un cliente para poder crear tu primera factura.
            </p>
          </div>
        </div>
      </div>

      {setupStatus.firstClient && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            <div>
              <h4 className="font-medium text-green-900">Ya tienes clientes registrados</h4>
              <p className="text-sm text-green-700">Puedes agregar otro o continuar al siguiente paso.</p>
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>RFC del Cliente *</Label>
              <Input 
                placeholder="XAXX010101000"
                value={clientData.rfc}
                onChange={(e) => setClientData(prev => ({ ...prev, rfc: e.target.value.toUpperCase() }))}
                maxLength={13}
              />
            </div>
            <div>
              <Label>Régimen Fiscal *</Label>
              <select 
                className="w-full border rounded-md p-2 h-10"
                value={clientData.regimen_fiscal}
                onChange={(e) => setClientData(prev => ({ ...prev, regimen_fiscal: e.target.value }))}
              >
                <option value="">Selecciona...</option>
                <option value="601">601 - General de Ley PM</option>
                <option value="612">612 - Actividades empresariales</option>
                <option value="626">626 - RESICO</option>
                <option value="616">616 - Sin obligaciones fiscales</option>
              </select>
            </div>
          </div>
          <div>
            <Label>Razón Social / Nombre *</Label>
            <Input 
              placeholder="Cliente Ejemplo SA de CV"
              value={clientData.razon_social}
              onChange={(e) => setClientData(prev => ({ ...prev, razon_social: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Código Postal *</Label>
              <Input 
                placeholder="44100"
                value={clientData.codigo_postal}
                onChange={(e) => setClientData(prev => ({ ...prev, codigo_postal: e.target.value }))}
                maxLength={5}
              />
            </div>
            <div>
              <Label>Uso CFDI *</Label>
              <select 
                className="w-full border rounded-md p-2 h-10"
                value={clientData.uso_cfdi}
                onChange={(e) => setClientData(prev => ({ ...prev, uso_cfdi: e.target.value }))}
              >
                <option value="G03">G03 - Gastos en general</option>
                <option value="G01">G01 - Adquisición de mercancías</option>
                <option value="G02">G02 - Devoluciones</option>
                <option value="I01">I01 - Construcciones</option>
                <option value="P01">P01 - Por definir</option>
                <option value="S01">S01 - Sin efectos fiscales</option>
              </select>
            </div>
          </div>
          <div>
            <Label>Email *</Label>
            <Input 
              type="email" 
              placeholder="cliente@ejemplo.com"
              value={clientData.email}
              onChange={(e) => setClientData(prev => ({ ...prev, email: e.target.value }))}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setCurrentStep(5)}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Atrás
        </Button>
        <div className="flex gap-2">
          {setupStatus.firstClient && (
            <Button variant="ghost" onClick={() => setCurrentStep(7)}>
              Omitir
            </Button>
          )}
          <Button onClick={handleSaveClient} disabled={saving} data-testid="save-client-btn">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
            ) : (
              <>Guardar y Continuar<ChevronRight className="w-4 h-4 ml-2" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  const renderWhatsAppStep = () => (
    <div className="space-y-6" data-testid="setup-whatsapp">
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <MessageSquare className="w-5 h-5 text-teal-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-teal-900">Asistente de WhatsApp</h4>
            <p className="text-sm text-teal-700">
              Configura cómo quieres que el bot te llame y con qué frecuencia enviarte recordatorios.
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-6">
          <div>
            <Label>¿Cómo te gustaría que te llame el bot?</Label>
            <Input 
              placeholder="Ej: Tony, Lic. García, Contador..."
              value={whatsappConfig.preferred_name}
              onChange={(e) => setWhatsappConfig(prev => ({ ...prev, preferred_name: e.target.value }))}
              className="mt-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              El bot usará este nombre para saludarte: "¡Hola {whatsappConfig.preferred_name || 'Tony'}! ¿En qué te ayudo?"
            </p>
          </div>

          <div>
            <Label>Recordatorios de Constancia de Situación Fiscal</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {['weekly', 'monthly', 'manual'].map((freq) => (
                <Button 
                  key={freq}
                  variant={whatsappConfig.csf_reminder === freq ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setWhatsappConfig(prev => ({ ...prev, csf_reminder: freq }))}
                >
                  {freq === 'weekly' ? 'Semanal' : freq === 'monthly' ? 'Mensual' : 'Manual'}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <Label>Recordatorios de Opinión de Cumplimiento (32-D)</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {['weekly', 'monthly', 'manual'].map((freq) => (
                <Button 
                  key={freq}
                  variant={whatsappConfig.opinion_reminder === freq ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setWhatsappConfig(prev => ({ ...prev, opinion_reminder: freq }))}
                >
                  {freq === 'weekly' ? 'Semanal' : freq === 'monthly' ? 'Mensual' : 'Manual'}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <Label>Tu número de WhatsApp</Label>
            <Input 
              placeholder="+52 33 1234 5678" 
              className="mt-2"
              value={whatsappConfig.phone || companyData.phone}
              onChange={(e) => setWhatsappConfig(prev => ({ ...prev, phone: e.target.value }))}
            />
            <p className="text-xs text-gray-500 mt-1">
              Aquí recibirás los recordatorios y podrás interactuar con el bot.
            </p>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Cómo activar el bot:</h4>
            <ol className="text-sm text-blue-700 space-y-1">
              <li>1. Abre WhatsApp y envía un mensaje a <strong>+1 415 523 8886</strong></li>
              <li>2. Escribe: <strong>join yearly-shape</strong></li>
              <li>3. ¡Listo! El bot te saludará y podrás empezar a usarlo</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setCurrentStep(6)}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Atrás
        </Button>
        <Button onClick={handleSaveWhatsAppConfig} disabled={saving} data-testid="finish-setup-btn">
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
          ) : (
            <>Finalizar Configuración<ChevronRight className="w-4 h-4 ml-2" /></>
          )}
        </Button>
      </div>
    </div>
  );

  const handleStartTour = () => {
    // Mark tour as pending and navigate to dashboard
    localStorage.setItem('cia_start_tour', 'true');
    navigate('/dashboard');
  };

  const renderComplete = () => (
    <div className="text-center space-y-6 py-8" data-testid="setup-complete">
      <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full mx-auto flex items-center justify-center">
        <CheckCircle2 className="w-12 h-12 text-white" />
      </div>
      <h2 className="text-3xl font-bold text-gray-900">¡Configuración Completada!</h2>
      <p className="text-gray-600 max-w-md mx-auto">
        Tu cuenta está lista. Ya puedes empezar a facturar desde el dashboard o directamente desde WhatsApp.
      </p>

      {/* Tour Suggestion */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 max-w-2xl mx-auto">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="text-left">
            <h4 className="font-semibold text-slate-900 mb-1">¿Quieres un tour guiado?</h4>
            <p className="text-sm text-slate-600 mb-3">
              Te mostramos las funcionalidades principales del dashboard en 2 minutos. 
              Ideal para conocer todas las herramientas disponibles.
            </p>
            <Button 
              onClick={handleStartTour}
              className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Iniciar Tour Guiado
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto mt-8">
        <Card 
          className="cursor-pointer hover:border-blue-400 transition-colors" 
          onClick={() => navigate('/dashboard/invoices/new')}
        >
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full mx-auto flex items-center justify-center mb-3">
              <FileKey className="w-6 h-6 text-blue-500" />
            </div>
            <h4 className="font-semibold">Crear Factura</h4>
            <p className="text-sm text-gray-500">Emite tu primera factura</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:border-green-400 transition-colors" 
          onClick={() => navigate('/dashboard/clients')}
        >
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full mx-auto flex items-center justify-center mb-3">
              <Users className="w-6 h-6 text-green-500" />
            </div>
            <h4 className="font-semibold">Agregar Clientes</h4>
            <p className="text-sm text-gray-500">Registra más clientes</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:border-purple-400 transition-colors" 
          onClick={() => {
            // Open WhatsApp
            window.open('https://wa.me/14155238886?text=Hola', '_blank');
          }}
        >
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full mx-auto flex items-center justify-center mb-3">
              <MessageSquare className="w-6 h-6 text-purple-500" />
            </div>
            <h4 className="font-semibold">Usar WhatsApp</h4>
            <p className="text-sm text-gray-500">Factura desde tu celular</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-center gap-4 mt-8">
        <Button 
          variant="outline" 
          onClick={() => window.open(`${API_URL}/api/docs/manual`, '_blank')}
        >
          <Download className="w-4 h-4 mr-2" />
          Descargar Manual
        </Button>
        <Button onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn">
          Ir al Dashboard
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderWelcome();
      case 1: return renderCompanyStep();
      case 2: return renderCertificatesStep();
      case 3: return renderSubscriptionStep();
      case 4: return renderBillingDataStep();
      case 5: return renderTaxConfigStep();
      case 6: return renderFirstClientStep();
      case 7: return renderWhatsAppStep();
      case 8: return renderComplete();
      default: return renderWelcome();
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Configuración Inicial</h1>
          <p className="text-gray-500">CIA Factura℗ - Sistema de Facturación Electrónica</p>
        </div>

        {/* Progress */}
        {currentStep > 0 && currentStep < 8 && (
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              {steps.slice(1, -1).map((step, index) => {
                const Icon = step.icon;
                const isActive = index + 1 === currentStep;
                const isComplete = index + 1 < currentStep;
                return (
                  <div 
                    key={step.id} 
                    className={`flex flex-col items-center ${isActive ? 'opacity-100' : 'opacity-50'}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      isComplete ? 'bg-green-500 text-white' : 
                      isActive ? 'bg-blue-500 text-white' : 'bg-gray-200'
                    }`}>
                      {isComplete ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <span className="text-xs mt-1 hidden md:block">{step.title}</span>
                  </div>
                );
              })}
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}

        {/* Step Content */}
        <Card className="shadow-lg">
          <CardHeader className={currentStep === 0 || currentStep === 8 ? 'hidden' : ''}>
            <CardTitle className="flex items-center gap-3">
              {steps[currentStep] && React.createElement(steps[currentStep].icon, {
                className: `w-6 h-6 ${steps[currentStep].color}`
              })}
              {steps[currentStep]?.title}
            </CardTitle>
            <CardDescription>{steps[currentStep]?.description}</CardDescription>
          </CardHeader>
          <CardContent className={currentStep === 0 || currentStep === 8 ? 'pt-6' : ''}>
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Help */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            ¿Necesitas ayuda? Escribe "ayuda" en el chat de WhatsApp o contacta a soporte.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
